import type { TargetPeriodType } from "@/grid/core/types";
import { supabase } from "@/lib/supabase";

export interface UpsertTargetParams {
  scopeType: "department" | "team_group";
  scopeKey: string;
  metric: string;
  periodType: TargetPeriodType;
  periodStart: string;
  targetValue: number;
}

export async function upsertTarget(params: UpsertTargetParams): Promise<void> {
  const { error } = await supabase.rpc("wh_upsert_target", {
    p_scope_type: params.scopeType,
    p_scope_key: params.scopeKey,
    p_scope_id: null,
    p_metric: params.metric,
    p_period_type: params.periodType,
    p_period_start: params.periodStart,
    p_target_value: params.targetValue,
  });

  if (error) throw error;
}

export async function deleteTarget(params: Omit<UpsertTargetParams, "targetValue">): Promise<void> {
  const { error } = await supabase.rpc("wh_delete_target", {
    p_scope_type: params.scopeType,
    p_scope_key: params.scopeKey,
    p_scope_id: null,
    p_metric: params.metric,
    p_period_type: params.periodType,
    p_period_start: params.periodStart,
  });

  if (error) throw error;
}

export async function fetchTargetsForPeriodStarts(
  periodType: TargetPeriodType,
  periodStarts: string[],
): Promise<
  Array<{
    scope_type: string;
    scope_key: string | null;
    scope_id: number | null;
    metric: string;
    period_type: string;
    period_start: string;
    target_value: number;
  }>
> {
  if (periodStarts.length === 0) return [];

  const { data, error } = await supabase
    .from("targets")
    .select("scope_type, scope_key, scope_id, metric, period_type, period_start, target_value")
    .eq("period_type", periodType)
    .in("period_start", periodStarts);

  if (error) throw error;

  return (data ?? [])
    .filter((row) => row.metric != null && row.target_value != null && row.scope_type != null)
    .map((row) => ({
      scope_type: row.scope_type as string,
      scope_key: row.scope_key,
      scope_id: row.scope_id,
      metric: row.metric as string,
      period_type: row.period_type as string,
      period_start: row.period_start as string,
      target_value: Number(row.target_value),
    }));
}

export async function fetchTargetsForPeriod(
  periodType: TargetPeriodType,
  periodStart: string,
): Promise<
  Array<{
    scope_type: string;
    scope_key: string | null;
    scope_id: number | null;
    metric: string;
    period_type: string;
    period_start: string;
    target_value: number;
  }>
> {
  return fetchTargetsForPeriodStarts(periodType, [periodStart]);
}
